"use client"

import { useEffect, useState } from "react"

import { useTranslations } from "next-intl"

import { Stack } from "@/components/ui"
import { useReferralShareActions } from "@/features/referrals/hooks/use-referral-share-actions"
import { useReferralTermsCopy } from "@/features/referrals/hooks/use-referral-terms-copy"
import { ReferralBenefits, ReferralDialog } from "@/features/referrals/ReferralDialog"
import { ReferralChannels, ReferralCodeRow, ReferralInstagramChooser, ReferralShareStatus } from "@/features/referrals/ReferralShareKit"
import type { ReferralOfferState } from "@/lib/hooks/data/use-referrals"

interface ReferralShareDialogProps {
  offer: Extract<ReferralOfferState, { status: "ready" }>
  open: boolean
  onClose: () => void
  imageUrls: string[]
  productTitle?: string
}

type View = "share" | "instagram"

export const ReferralShareDialog = ({ offer, open, onClose, imageUrls, productTitle }: ReferralShareDialogProps) => {
  const t = useTranslations("referrals")
  const [view, setView] = useState<View>("share")
  const [shownOpen, setShownOpen] = useState(open)
  if (open !== shownOpen) {
    setShownOpen(open)
    if (open) setView("share")
  }

  const existing = offer.share
  const { ensureShare } = offer
  const shouldCreate = open && existing === null && !offer.creating && offer.creationError === null

  useEffect(() => {
    if (shouldCreate) void ensureShare().catch(() => undefined)
  }, [shouldCreate, ensureShare])

  const copy = useReferralTermsCopy(offer.me.terms)
  const actions = useReferralShareActions({ share: existing, imageUrls, discount: copy.discount, productTitle })

  const codeFailed = !existing && offer.creationError !== null
  const onCode = existing ? actions.copyCode : () => void ensureShare().catch(() => undefined)

  const toggleInstagram = () => {
    if (view === "instagram") {
      setView("share")
      return
    }
    actions.prepareStory()
    setView("instagram")
  }

  return (
    <ReferralDialog open={open} onClose={onClose} testId="referral-share-dialog" imageUrls={imageUrls} titleLead={copy.titleLead} titleEmphasis={copy.titleEmphasis}>
      <Stack gap="400">
        {view === "share" ? (
          <>
            <ReferralBenefits key="share" benefits={copy.benefits} />
            <ReferralCodeRow
              code={existing?.code ?? (codeFailed ? t("dialog.codeFailed") : t("dialog.preparing"))}
              copied={actions.feedback === "code"}
              pending={!existing && offer.creating}
              failed={codeFailed}
              onCopy={onCode}
            />
          </>
        ) : (
          <ReferralInstagramChooser key="instagram" actions={actions} body={copy.instagramBody} />
        )}

        <ReferralChannels actions={actions} instagramOpen={view === "instagram"} onInstagram={toggleInstagram} />

        <ReferralShareStatus actions={actions} error={offer.creationError ? t("dialog.codeFailed") : null} />
      </Stack>
    </ReferralDialog>
  )
}
